import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import type {
  RecurringReminderTemplate,
  RecurringTemplateWithClient,
} from '../types/recurring-reminders';
import type {
  CreateRecurringReminderInput,
  UpdateRecurringReminderInput,
} from '@/lib/validations/recurring-reminders';

/**
 * Hook to create a new recurring reminder template
 */
export function useCreateRecurringTemplate(clientId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRecurringReminderInput) => {
      return apiRequest<RecurringTemplateWithClient>(
        `/api/clients/${clientId}/reminders/recurring`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      // Invalidate both templates and reminders (since instances are generated)
      queryClient.invalidateQueries({
        queryKey: ['recurring-templates', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['reminders', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['all-reminders'],
      });
    },
  });
}

/**
 * Hook to update a recurring reminder template
 */
export function useUpdateRecurringTemplate(clientId: string, templateId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateRecurringReminderInput) => {
      return apiRequest<RecurringTemplateWithClient>(
        `/api/clients/${clientId}/reminders/recurring/${templateId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      // Invalidate templates, specific template, and reminders
      queryClient.invalidateQueries({
        queryKey: ['recurring-templates', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['recurring-template', clientId, templateId],
      });
      queryClient.invalidateQueries({
        queryKey: ['reminders', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['all-reminders'],
      });
    },
  });
}

/**
 * Hook to delete a recurring reminder template
 */
export function useDeleteRecurringTemplate(clientId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      return apiRequest<{ success: boolean }>(
        `/api/clients/${clientId}/reminders/recurring/${templateId}`,
        {
          method: 'DELETE',
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['recurring-templates', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['reminders', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['all-reminders'],
      });
    },
  });
}

/**
 * Hook to pause a recurring reminder template
 */
export function usePauseRecurringTemplate(clientId: string, templateId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return apiRequest<{ success: boolean; message: string }>(
        `/api/clients/${clientId}/reminders/recurring/${templateId}/pause`,
        {
          method: 'POST',
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['recurring-templates', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['recurring-template', clientId, templateId],
      });
    },
  });
}

/**
 * Hook to resume a paused recurring reminder template
 */
export function useResumeRecurringTemplate(clientId: string, templateId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return apiRequest<{ success: boolean; message: string }>(
        `/api/clients/${clientId}/reminders/recurring/${templateId}/resume`,
        {
          method: 'POST',
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      // Invalidate templates and reminders (new instances may be generated)
      queryClient.invalidateQueries({
        queryKey: ['recurring-templates', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['recurring-template', clientId, templateId],
      });
      queryClient.invalidateQueries({
        queryKey: ['reminders', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['all-reminders'],
      });
    },
  });
}

/**
 * Combined hook that provides all recurring template mutations
 */
export function useRecurringTemplateMutations(clientId: string, templateId?: string) {
  const createMutation = useCreateRecurringTemplate(clientId);
  const updateMutation = useUpdateRecurringTemplate(clientId, templateId || '');
  const deleteMutation = useDeleteRecurringTemplate(clientId);
  const pauseMutation = usePauseRecurringTemplate(clientId, templateId || '');
  const resumeMutation = useResumeRecurringTemplate(clientId, templateId || '');

  return {
    create: createMutation,
    update: updateMutation,
    delete: deleteMutation,
    pause: pauseMutation,
    resume: resumeMutation,
  };
}
